import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Lock, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await api.resetPassword(token, password);
      toast.success("Password updated — please sign in");
      navigate("/login");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full border border-white/15 bg-slate-950 px-4 py-3 text-white outline-none transition-colors duration-200 placeholder:text-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500";

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-slate-900 p-6">
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[400px] w-[500px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[120px]" />
      <div className="relative w-full max-w-md border border-white/10 bg-slate-950 p-10">
        <div className="mb-8">
          <img src="/logo.png" alt="Ticker Templates" className="h-11 w-auto" />
        </div>
        <h1 className="font-display text-3xl font-black uppercase tracking-tight text-white">New password</h1>
        {!token ? (
          <p className="mt-3 text-sm text-red-400" data-testid="reset-no-token">
            This reset link is missing its token. Please request a new link from the sign-in page.
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm text-slate-400">Choose a strong new password for your admin account.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-wider text-slate-500">New password</label>
                <input data-testid="reset-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={input} />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Confirm password</label>
                <input data-testid="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className={input} />
              </div>
              <button data-testid="reset-submit" disabled={loading} className="flex w-full items-center justify-center gap-2 bg-emerald-500 px-6 py-3.5 font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400 disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                {loading ? "Updating..." : "Update password"}
              </button>
            </form>
          </>
        )}
        <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors duration-200 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
